import React, { useEffect, useState } from 'react';
import {
  createEvent,
  createCompensation,
  fetchEvents,
  fetchCompensations,
  type InsuranceEvent,
  type Compensation,
} from '../api/insurance';

const EVENT_TYPES = ['failure', 'timeout', 'quality_issue', 'slashing'] as const;
const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const;

export default function Claims() {
  const [tab, setTab] = useState<'events' | 'compensations'>('events');
  const [events, setEvents] = useState<InsuranceEvent[]>([]);
  const [compensations, setCompensations] = useState<Compensation[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState<{ type: string; msg: string } | null>(null);

  const [eventForm, setEventForm] = useState({
    productId: '',
    agentId: '',
    type: 'failure' as InsuranceEvent['type'],
    severity: 'medium' as InsuranceEvent['severity'],
    description: '',
  });

  const [compForm, setCompForm] = useState({
    eventId: '',
    amount: '',
    recipient: '',
  });

  const load = async () => {
    try {
      const [ev, comp] = await Promise.all([fetchEvents(), fetchCompensations()]);
      setEvents(ev);
      setCompensations(comp);
    } catch {
      /* keep current */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!eventForm.productId || !eventForm.agentId || !eventForm.description) {
      setAlert({ type: 'warning', msg: 'All fields are required.' });
      return;
    }
    setSubmitting(true);
    setAlert(null);
    try {
      await createEvent(eventForm);
      setAlert({ type: 'success', msg: 'Event reported successfully.' });
      setEventForm({ productId: '', agentId: '', type: 'failure', severity: 'medium', description: '' });
      await load();
    } catch (e) {
      setAlert({ type: 'warning', msg: String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const handleComp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!compForm.eventId || !compForm.amount || !compForm.recipient) {
      setAlert({ type: 'warning', msg: 'All fields are required.' });
      return;
    }
    setSubmitting(true);
    setAlert(null);
    try {
      await createCompensation({
        eventId: compForm.eventId,
        amount: Number(compForm.amount),
        recipient: compForm.recipient,
        status: 'pending',
      });
      setAlert({ type: 'success', msg: 'Compensation request submitted.' });
      setCompForm({ eventId: '', amount: '', recipient: '' });
      await load();
    } catch (e) {
      setAlert({ type: 'warning', msg: String(e) });
    } finally {
      setSubmitting(false);
    }
  };

  const severityBadge = (s: string) => {
    const cls = s === 'critical' || s === 'high' ? 'warning' : s === 'medium' ? 'active' : 'safe';
    return <span className={`insurance-badge ${cls}`}>{s}</span>;
  };

  const compStatusBadge = (s: string) => {
    const cls = s === 'paid' || s === 'approved' ? 'safe' : s === 'pending' ? 'active' : 'warning';
    return <span className={`insurance-badge ${cls}`}>{s}</span>;
  };

  return (
    <div>
      <header className="insurance-header">
        <h1>Claims & Events</h1>
        <p>Report incidents and file compensation claims</p>
      </header>

      {alert && (
        <div className={`insurance-alert ${alert.type}`}>{alert.msg}</div>
      )}

      {/* ── Tab Switch ── */}
      <div className="insurance-tabs" style={{ marginBottom: 24 }}>
        <button
          className={`insurance-tab ${tab === 'events' ? 'active' : ''}`}
          onClick={() => setTab('events')}
        >
          ⚡ Events ({events.length})
        </button>
        <button
          className={`insurance-tab ${tab === 'compensations' ? 'active' : ''}`}
          onClick={() => setTab('compensations')}
        >
          💰 Compensations ({compensations.length})
        </button>
      </div>

      {tab === 'events' ? (
        <>
          {/* ── Report Event ── */}
          <div className="insurance-card" style={{ marginBottom: 24 }}>
            <div className="insurance-card-header">
              <span className="insurance-card-title">
                <span className="insurance-icon">⚡</span>
                Report Event
              </span>
            </div>
            <form onSubmit={handleEvent} className="insurance-form">
              <div className="insurance-form-row">
                <div className="insurance-form-group">
                  <label className="insurance-label">Product ID</label>
                  <input
                    className="insurance-input"
                    placeholder="product-001"
                    value={eventForm.productId}
                    onChange={(e) => setEventForm({ ...eventForm, productId: e.target.value })}
                  />
                </div>
                <div className="insurance-form-group">
                  <label className="insurance-label">Agent ID</label>
                  <input
                    className="insurance-input"
                    placeholder="agent-alpha"
                    value={eventForm.agentId}
                    onChange={(e) => setEventForm({ ...eventForm, agentId: e.target.value })}
                  />
                </div>
              </div>
              <div className="insurance-form-row">
                <div className="insurance-form-group">
                  <label className="insurance-label">Type</label>
                  <select
                    className="insurance-input"
                    value={eventForm.type}
                    onChange={(e) => setEventForm({ ...eventForm, type: e.target.value as any })}
                  >
                    {EVENT_TYPES.map((t) => (
                      <option key={t} value={t}>{t.replace('_', ' ')}</option>
                    ))}
                  </select>
                </div>
                <div className="insurance-form-group">
                  <label className="insurance-label">Severity</label>
                  <select
                    className="insurance-input"
                    value={eventForm.severity}
                    onChange={(e) => setEventForm({ ...eventForm, severity: e.target.value as any })}
                  >
                    {SEVERITIES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="insurance-form-group">
                <label className="insurance-label">Description</label>
                <textarea
                  className="insurance-input insurance-textarea"
                  rows={3}
                  placeholder="Describe the incident…"
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                />
              </div>
              <button className="insurance-button" type="submit" disabled={submitting}>
                {submitting ? 'Reporting…' : '⚡ Report Event'}
              </button>
            </form>
          </div>

          {/* ── Event List ── */}
          <div className="insurance-card">
            <div className="insurance-card-header">
              <span className="insurance-card-title">
                <span className="insurance-icon">📋</span>
                All Events
              </span>
              <span className="insurance-badge active">{events.length}</span>
            </div>
            {loading ? (
              <div className="insurance-loading">Loading…</div>
            ) : events.length === 0 ? (
              <div className="insurance-empty">No events recorded.</div>
            ) : (
              <table className="insurance-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Agent</th>
                    <th>Severity</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((ev, i) => (
                    <tr key={i}>
                      <td><span className="insurance-badge active">{ev.type}</span></td>
                      <td className="mono">{ev.agentId}</td>
                      <td>{severityBadge(ev.severity)}</td>
                      <td>{ev.description || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <>
          {/* ── File Compensation ── */}
          <div className="insurance-card" style={{ marginBottom: 24 }}>
            <div className="insurance-card-header">
              <span className="insurance-card-title">
                <span className="insurance-icon">💰</span>
                File Compensation
              </span>
            </div>
            <form onSubmit={handleComp} className="insurance-form">
              <div className="insurance-form-row">
                <div className="insurance-form-group">
                  <label className="insurance-label">Event ID</label>
                  <input
                    className="insurance-input"
                    placeholder="event-001"
                    value={compForm.eventId}
                    onChange={(e) => setCompForm({ ...compForm, eventId: e.target.value })}
                  />
                </div>
                <div className="insurance-form-group">
                  <label className="insurance-label">Amount (AGU)</label>
                  <input
                    className="insurance-input"
                    type="number"
                    placeholder="500"
                    value={compForm.amount}
                    onChange={(e) => setCompForm({ ...compForm, amount: e.target.value })}
                  />
                </div>
              </div>
              <div className="insurance-form-group">
                <label className="insurance-label">Recipient (Wallet / Agent ID)</label>
                <input
                  className="insurance-input"
                  placeholder="0x1234… or agent-beta"
                  value={compForm.recipient}
                  onChange={(e) => setCompForm({ ...compForm, recipient: e.target.value })}
                />
              </div>
              <button className="insurance-button" type="submit" disabled={submitting}>
                {submitting ? 'Submitting…' : '💰 File Compensation'}
              </button>
            </form>
          </div>

          {/* ── Compensation List ── */}
          <div className="insurance-card">
            <div className="insurance-card-header">
              <span className="insurance-card-title">
                <span className="insurance-icon">📋</span>
                All Compensations
              </span>
              <span className="insurance-badge safe">{compensations.length}</span>
            </div>
            {loading ? (
              <div className="insurance-loading">Loading…</div>
            ) : compensations.length === 0 ? (
              <div className="insurance-empty">No compensation claims filed.</div>
            ) : (
              <table className="insurance-table">
                <thead>
                  <tr>
                    <th>Event</th>
                    <th>Recipient</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {compensations.map((c, i) => (
                    <tr key={i}>
                      <td className="mono">{c.eventId}</td>
                      <td className="mono">{c.recipient}</td>
                      <td>{c.amount} AGU</td>
                      <td>{compStatusBadge(c.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}
